import type { CollaborationFormat, GetDocumentResponse } from "@workith/shared";
import {
  ANONYMOUS_COLLABORATION_TOKEN,
  canWriteForRole,
  LEGACY_COLLABORATION_FORMAT,
  SECTIONED_COLLABORATION_FORMAT,
} from "@workith/shared";
import { Button } from "@workith/ui";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CollaborativeEditor from "../components/collaboration/CollaborativeEditor";
import SectionedCollaborativeEditor from "../components/collaboration/SectionedCollaborativeEditor";
import api from "../lib/api";
import { getGatewayNavigationUrl } from "../lib/backendUrl";

export type EditorPageShellMode = "app" | "standalone";

interface EditorPageProps {
  shellMode: EditorPageShellMode;
}

interface DocumentEditAccess {
  documentId: string;
  canEdit: boolean;
  collaborationFormat: CollaborationFormat;
}

export function EditorPage({ shellMode }: EditorPageProps) {
  const { documentId } = useParams<{ documentId: string }>();
  const resolvedDocumentId = documentId || "default";
  const isAppShell = shellMode === "app";
  // Start read-only: the editor must not be writable before the access check
  // resolves, otherwise a viewer could type edits that the read-only server
  // connection silently rejects (looking like lost work).
  const [editAccess, setEditAccess] = useState<DocumentEditAccess | null>(null);
  const resolvedAccess = editAccess?.documentId === resolvedDocumentId ? editAccess : null;
  const canEdit = resolvedAccess?.canEdit ?? false;
  // Until the metadata resolves the document is treated as legacy and read-only:
  // the sectioned editor must never bind a Tiptap document to a root manifest.
  const collaborationFormat = resolvedAccess?.collaborationFormat ?? LEGACY_COLLABORATION_FORMAT;
  const collaborationTokenProvider = useCallback(async () => {
    if (!isAppShell) return ANONYMOUS_COLLABORATION_TOKEN;
    return api.post<{ documentId: string }, { token: string; expiresAt: number }>(
      "/api/collaboration-tokens",
      { documentId: resolvedDocumentId }
    );
  }, [isAppShell, resolvedDocumentId]);

  useEffect(() => {
    let isActive = true;
    setEditAccess(null);

    async function loadDocumentAccess() {
      try {
        const accessPath = isAppShell
          ? `/api/documents/${resolvedDocumentId}/access`
          : `/api/documents/${resolvedDocumentId}`;
        const response = await api.get<GetDocumentResponse>(accessPath);
        if (!isActive) {
          return;
        }

        setEditAccess({
          documentId: resolvedDocumentId,
          canEdit: canWriteForRole(response.document.user_role),
          collaborationFormat: response.document.collaboration_format,
        });
      } catch (error) {
        console.error("Failed to load document access:", error);
        if (isActive) {
          setEditAccess(null);
        }
      }
    }

    loadDocumentAccess();

    return () => {
      isActive = false;
    };
  }, [isAppShell, resolvedDocumentId]);

  const editorProps = {
    documentId: resolvedDocumentId,
    canEdit,
    collaborationTokenProvider,
    toolbarMode: isAppShell ? ("sticky-main" as const) : ("inline" as const),
  };

  // `legacy-single-v1` keeps the unchanged single-provider path; only
  // `sectioned-v1` opens one room per section.
  const editor =
    collaborationFormat === SECTIONED_COLLABORATION_FORMAT ? (
      <SectionedCollaborativeEditor {...editorProps} />
    ) : (
      <CollaborativeEditor {...editorProps} />
    );

  if (isAppShell) {
    return <div className="min-h-full bg-muted/40">{editor}</div>;
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="border-b bg-background px-4 py-2 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Viewing public document</div>
        <Button
          onClick={() => {
            window.location.href = getGatewayNavigationUrl("login");
          }}
        >
          Sign In
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">{editor}</div>
    </div>
  );
}
